import { useState, useCallback, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { AnimatePresence } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { Toaster, toast } from "sonner";

// Constants & utilities
import { API, STATES, DISH_TYPES, LOADING_MESSAGES, SCANNING_MESSAGES } from "@/constants";
import { classifyIngredient, parseIngredientWithQuantity, optimizeImage, isBlocklisted } from "@/utils/ingredients";

// Screen components
import { HomeScreen } from "@/components/screens/HomeScreen";
import { ScanningScreen } from "@/components/screens/ScanningScreen";
import { IngredientsScreen } from "@/components/screens/IngredientsScreen";
import { VibeScreen } from "@/components/screens/VibeScreen";
import { GeneratingScreen } from "@/components/screens/GeneratingScreen";

// Modal components
import { RecipeDetailModal } from "@/components/modals/RecipeDetailModal";
import { SavedRecipesModal } from "@/components/modals/SavedRecipesModal";
import { HistoryModal } from "@/components/modals/HistoryModal";
import { DessertConfirmModal } from "@/components/modals/DessertConfirmModal";
import { EditQuantityModal } from "@/components/modals/EditQuantityModal";

// Lazy-loaded components
const FeedbackWall = lazy(() => import("./components/FeedbackWall"));
const SwipeableRecipeViewer = lazy(() => import("./components/SwipeableRecipeViewer"));

function App() {
  // ─── Core App State ───
  const [appState, setAppState] = useState(STATES.HOME);
  const [ingredients, setIngredients] = useState([]);
  const [ingredientsWithQty, setIngredientsWithQty] = useState([]);
  const [categorizedIngredients, setCategorizedIngredients] = useState(null);
  const [categorizedWithQty, setCategorizedWithQty] = useState(null);
  const [selectedVibe, setSelectedVibe] = useState(null);
  const [dietModes, setDietModes] = useState({ vegetarian: false, vegan: false, glutenFree: false, keto: false });
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [selectedDishType, setSelectedDishType] = useState("main_course");
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [loadingStage, setLoadingStage] = useState("");

  // ─── Ingredient Input State ───
  const [newIngredient, setNewIngredient] = useState("");
  const [newIngredientCategory, setNewIngredientCategory] = useState("pantry_other");
  const [bulkInput, setBulkInput] = useState("");

  // ─── UI State ───
  const [safetyMessage, setSafetyMessage] = useState(null);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [recipeHistory, setRecipeHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [copiedRecipeId, setCopiedRecipeId] = useState(null);
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState(null);
  const [currentScanningMessage, setCurrentScanningMessage] = useState(null);

  // ─── Feedback State ───
  const [showFeedbackWall, setShowFeedbackWall] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState(null);
  const [feedbackRecipe, setFeedbackRecipe] = useState(null);
  const [feedbackContext, setFeedbackContext] = useState(null);

  // ─── Modal State ───
  const [showDessertConfirm, setShowDessertConfirm] = useState(false);
  const [dessertIngredients, setDessertIngredients] = useState([]);
  const [newDessertIngredient, setNewDessertIngredient] = useState("");
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [editQuantity, setEditQuantity] = useState("");
  const [editUnit, setEditUnit] = useState("");
  const [validatingIngredient, setValidatingIngredient] = useState(false);

  // ─── Effects ───

  // Load saved recipes and history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("moodfood_saved_recipes");
    if (saved) setSavedRecipes(JSON.parse(saved));

    const history = localStorage.getItem("moodfood_recipe_history");
    if (history) {
      const parsed = JSON.parse(history);
      const cleaned = parsed.filter(entry =>
        entry.recipes?.length > 0 && entry.recipes.some(r => r.image_url?.length > 0)
      );
      if (cleaned.length !== parsed.length) {
        localStorage.setItem("moodfood_recipe_history", JSON.stringify(cleaned));
      }
      setRecipeHistory(cleaned);
    }
  }, []);

  // Rotate scanning messages
  useEffect(() => {
    if (appState !== STATES.SCANNING) { setCurrentScanningMessage(null); return; }
    setCurrentScanningMessage(SCANNING_MESSAGES[Math.floor(Math.random() * SCANNING_MESSAGES.length)]);
    const interval = setInterval(() => {
      setCurrentScanningMessage(SCANNING_MESSAGES[Math.floor(Math.random() * SCANNING_MESSAGES.length)]);
    }, 5000 + Math.random() * 2000);
    return () => clearInterval(interval);
  }, [appState]);

  // Rotate loading messages
  useEffect(() => {
    if (!(appState === STATES.GENERATING && loadingStage.includes("Creating"))) { setCurrentLoadingMessage(null); return; }
    setCurrentLoadingMessage(LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]);
    const interval = setInterval(() => {
      setCurrentLoadingMessage(LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]);
    }, 8000 + Math.random() * 2000);
    return () => clearInterval(interval);
  }, [appState, loadingStage]);

  // ─── Feedback Handlers ───
  const openRecipeFeedback = (recipe) => {
    setSelectedRecipe(null);
    setFeedbackCategory("recipe");
    setFeedbackRecipe(recipe);
    setFeedbackContext(null);
    setTimeout(() => setShowFeedbackWall(true), 100);
  };

  const openErrorFeedback = (errorMessage) => {
    setSelectedRecipe(null);
    setFeedbackCategory("error");
    setFeedbackRecipe(null);
    setFeedbackContext(`Error: ${errorMessage}\nVibe: ${selectedVibe || "Not selected"}\nCuisine: ${selectedCuisine || "Any"}\nDish Type: ${selectedDishType || "Any"}`);
    setTimeout(() => setShowFeedbackWall(true), 100);
  };

  const openFeedback = () => {
    setSelectedRecipe(null);
    setFeedbackCategory(null);
    setFeedbackRecipe(null);
    setFeedbackContext(null);
    setShowFeedbackWall(true);
  };

  // ─── Share & Copy ───
  const buildShareText = (recipe) =>
    `\u{1F37D}\u{FE0F} ${recipe.title}\n\u{23F1}\u{FE0F} ${recipe.time_minutes} min | \u{1F525} ${recipe.calories} cal\n\n\u{1F4DD} Ingredients:\n${recipe.ingredients.map(i => `\u2022 ${i.amount} ${i.name}`).join('\n')}\n\n\u{1F468}\u200D\u{1F373} Steps:\n${recipe.steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}\n\nMade with MoodFoodz`;

  const shareRecipe = async (recipe, e) => {
    if (e) e.stopPropagation();
    const shareText = buildShareText(recipe);
    if (navigator.share) {
      try { await navigator.share({ title: recipe.title, text: shareText }); toast.success("Recipe shared!"); }
      catch (err) { if (err.name !== 'AbortError') copyRecipeToClipboard(recipe, e); }
    } else { copyRecipeToClipboard(recipe, e); }
  };

  const copyRecipeToClipboard = async (recipe, e) => {
    if (e) e.stopPropagation();
    const shareText = buildShareText(recipe);
    let copied = false;
    if (navigator.clipboard && window.isSecureContext) {
      try { await navigator.clipboard.writeText(shareText); copied = true; } catch (_) {}
    }
    if (!copied) {
      try {
        const ta = document.createElement('textarea');
        ta.value = shareText;
        Object.assign(ta.style, { position: 'fixed', left: '0', top: '0', opacity: '0', pointerEvents: 'none' });
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        copied = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_) {}
    }
    if (copied) {
      setCopiedRecipeId(recipe.id);
      toast.success("Recipe copied to clipboard!");
      setTimeout(() => setCopiedRecipeId(null), 2000);
    } else {
      toast.info("Long press to select and copy the recipe text", { duration: 3000 });
      setSelectedRecipe(recipe);
    }
  };

  // ─── History & Saved ───
  const addToHistory = (newRecipes, vibe, diets) => {
    const recipesForStorage = newRecipes.map(recipe => {
      const isBase64 = recipe.image_url?.startsWith('data:');
      return (isBase64 && recipe.thumbnail_url)
        ? { ...recipe, image_url: recipe.thumbnail_url, thumbnail_url: recipe.thumbnail_url }
        : recipe;
    });
    const entry = { id: Date.now().toString(), recipes: recipesForStorage, vibe, diets, createdAt: new Date().toISOString() };
    const newHistory = [entry, ...recipeHistory].slice(0, 10);
    setRecipeHistory(newHistory);
    try {
      localStorage.setItem("moodfood_recipe_history", JSON.stringify(newHistory));
    } catch (e) {
      const stripped = newHistory.map(en => ({ ...en, recipes: en.recipes.map(r => ({ ...r, image_url: null, thumbnail_url: null })) }));
      setRecipeHistory(stripped);
      try { localStorage.setItem("moodfood_recipe_history", JSON.stringify(stripped)); }
      catch (_) { localStorage.removeItem("moodfood_recipe_history"); setRecipeHistory([]); }
    }
  };

  const toggleSaveRecipe = (recipe, e) => {
    if (e) e.stopPropagation();
    const isAlreadySaved = savedRecipes.some(r => r.id === recipe.id);
    let newSaved;
    if (isAlreadySaved) {
      newSaved = savedRecipes.filter(r => r.id !== recipe.id);
      toast.success("Recipe removed from favorites");
    } else {
      const isBase64 = recipe.image_url?.startsWith('data:');
      newSaved = [...savedRecipes, {
        ...recipe,
        image_url: (isBase64 && recipe.thumbnail_url) ? recipe.thumbnail_url : recipe.image_url,
        thumbnail_url: recipe.thumbnail_url,
        savedAt: new Date().toISOString()
      }];
      toast.success("Recipe saved to favorites!");
    }
    setSavedRecipes(newSaved);
    try { localStorage.setItem("moodfood_saved_recipes", JSON.stringify(newSaved)); }
    catch (_) { toast.error("Storage full. Please remove some saved recipes."); }
  };

  const isRecipeSaved = (recipeId) => savedRecipes.some(r => r.id === recipeId);

  // ─── Image Upload ───
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const remaining = 4 - uploadedImages.length;
    const filesToAdd = acceptedFiles.slice(0, remaining);
    if (acceptedFiles.length > remaining) toast.info(`Only ${remaining} more image(s) can be added. Max 4 images.`);
    const optimized = await Promise.all(filesToAdd.map(async (file) => {
      const blob = await optimizeImage(file);
      return { file: blob, originalName: file.name, preview: URL.createObjectURL(blob) };
    }));
    setUploadedImages(prev => [...prev, ...optimized]);
  }, [uploadedImages.length]);

  const removeUploadedImage = (index) => {
    setUploadedImages(prev => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[index].preview);
      copy.splice(index, 1);
      return copy;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".webp"] },
    maxFiles: 4,
    disabled: uploadedImages.length >= 4
  });

  // ─── Analyze Images ───
  const analyzeImages = async () => {
    if (uploadedImages.length === 0) return;
    setAppState(STATES.SCANNING);
    setLoadingStage("Peeking inside... \u{1F955}");
    try {
      const formData = new FormData();
      uploadedImages.forEach((img) => formData.append("files", img.file));
      const response = await axios.post(`${API}/analyze-images`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setLoadingStage("Organizing your ingredients...");
      await new Promise(r => setTimeout(r, 500));

      const foodItems = response.data.food_safe || [];
      if (foodItems.length === 0) {
        toast.error("No food ingredients found in the image(s). Please try with a different photo.");
        setAppState(STATES.HOME);
        setLoadingStage("");
        return;
      }

      if (response.data.categorized) setCategorizedIngredients(response.data.categorized);
      if (response.data.categorized_with_quantity) {
        setCategorizedWithQty(response.data.categorized_with_quantity);
        const allWithQty = Object.values(response.data.categorized_with_quantity).flat();
        setIngredientsWithQty(allWithQty);
      }
      setIngredients(foodItems);
      setSafetyMessage(response.data.message);
      setAppState(STATES.INGREDIENTS);
      setLoadingStage("");
      if (response.data.message) toast.info(response.data.message);
    } catch (error) {
      console.error("Error analyzing images:", error);
      toast.error("Failed to analyze images. Please try again.");
      setAppState(STATES.HOME);
      setLoadingStage("");
    }
  };

  // ─── Ingredient Management ───
  const addIngredient = async () => {
    const trimmed = newIngredient.trim();
    if (!trimmed) return;
    if (isBlocklisted(trimmed)) {
      toast.error(`"${trimmed}" is not a food ingredient.`);
      return;
    }
    // AI validation via backend
    setValidatingIngredient(true);
    let aiCategory = null;
    try {
      const res = await axios.post(`${API}/validate-ingredients`, { ingredients: [trimmed] });
      const result = res.data.results?.[trimmed.toLowerCase()];
      if (!result || !result.valid) {
        toast.error(`"${trimmed}" doesn't appear to be a food ingredient.`);
        setValidatingIngredient(false);
        return;
      }
      aiCategory = result.category || null;
    } catch {
      // If validation API fails, allow the ingredient through (fail-open)
    }
    setValidatingIngredient(false);

    const targetCategory = aiCategory || newIngredientCategory;

    if (categorizedWithQty) {
      if (Object.values(categorizedWithQty).flat().some(i => i.name.toLowerCase() === trimmed.toLowerCase())) {
        toast.info("Ingredient already exists!"); return;
      }
      const obj = { name: trimmed, quantity: null, unit: null, display: trimmed };
      setCategorizedWithQty(prev => ({ ...prev, [targetCategory]: [...(prev[targetCategory] || []), obj].sort((a, b) => a.name.localeCompare(b.name)) }));
      setCategorizedIngredients(prev => ({ ...prev, [targetCategory]: [...(prev[targetCategory] || []), trimmed].sort() }));
      setIngredients(prev => [...prev, trimmed]);
      setIngredientsWithQty(prev => [...prev, obj]);
    } else if (categorizedIngredients) {
      if (Object.values(categorizedIngredients).flat().some(i => i.toLowerCase() === trimmed.toLowerCase())) {
        toast.info("Ingredient already exists!"); return;
      }
      setCategorizedIngredients(prev => ({ ...prev, [targetCategory]: [...(prev[targetCategory] || []), trimmed].sort() }));
      setIngredients(prev => [...prev, trimmed]);
    } else {
      if (!ingredients.includes(trimmed)) setIngredients([...ingredients, trimmed]);
    }
    setNewIngredient("");
    toast.success(`Added ${trimmed}!`);
  };

  const addBulkIngredients = async () => {
    const items = bulkInput.split(',').map(s => s.trim()).filter(s => s.length > 0);
    if (items.length === 0) { toast.info("Please enter some ingredients separated by commas"); return; }
    const existing = categorizedWithQty ? Object.values(categorizedWithQty).flat().map(i => i.name.toLowerCase()) : ingredients.map(i => i.toLowerCase());

    // Parse all items and separate blocklisted ones
    const parsed = items.map(item => parseIngredientWithQuantity(item)).filter(Boolean);
    const blocklisted = [];
    const toValidate = [];
    parsed.forEach(p => {
      if (isBlocklisted(p.name)) { blocklisted.push(p.name); }
      else { toValidate.push(p); }
    });

    // AI-validate remaining items via backend
    let validItems = {}; // name -> category
    if (toValidate.length > 0) {
      setValidatingIngredient(true);
      try {
        const res = await axios.post(`${API}/validate-ingredients`, { ingredients: toValidate.map(p => p.name) });
        const results = res.data.results || {};
        Object.entries(results).forEach(([name, data]) => {
          if (data.valid) validItems[name] = data.category || "pantry_other";
        });
      } catch {
        // Fail-open: if API fails, accept all non-blocklisted items
        toValidate.forEach(p => { validItems[p.name.toLowerCase()] = "pantry_other"; });
      }
      setValidatingIngredient(false);
    }

    let added = 0, skipped = 0;
    const nonFood = [...blocklisted];
    const newCWQ = { ...categorizedWithQty }, newCI = { ...categorizedIngredients }, newIL = [...ingredients], newIWQ = [...ingredientsWithQty];
    toValidate.forEach(p => {
      const aiCat = validItems[p.name.toLowerCase()];
      if (!aiCat) { nonFood.push(p.name); return; }
      if (existing.includes(p.name.toLowerCase())) { skipped++; return; }
      const cat = aiCat;
      const obj = { name: p.name, quantity: p.quantity, unit: p.unit, display: p.display };
      if (!newCWQ[cat]) newCWQ[cat] = [];
      newCWQ[cat].push(obj);
      if (!newCI[cat]) newCI[cat] = [];
      newCI[cat].push(p.name);
      newIL.push(p.name);
      newIWQ.push(obj);
      existing.push(p.name.toLowerCase());
      added++;
    });
    Object.keys(newCWQ).forEach(c => {
      if (newCWQ[c] && Array.isArray(newCWQ[c])) {
        newCWQ[c] = newCWQ[c].filter(item => item && item.name).sort((a, b) => a.name.localeCompare(b.name));
      }
    });
    Object.keys(newCI).forEach(c => {
      if (newCI[c] && Array.isArray(newCI[c])) {
        newCI[c] = newCI[c].filter(item => item).sort();
      }
    });
    setCategorizedWithQty(newCWQ);
    setCategorizedIngredients(newCI);
    setIngredients(newIL);
    setIngredientsWithQty(newIWQ);
    setBulkInput("");
    if (nonFood.length > 0) toast.error(`Not food ingredients: ${nonFood.join(', ')}`);
    if (added > 0 && skipped > 0) toast.success(`Added ${added} ingredient${added > 1 ? 's' : ''}! (${skipped} duplicate${skipped > 1 ? 's' : ''} skipped)`);
    else if (added > 0) toast.success(`Added ${added} ingredient${added > 1 ? 's' : ''}!`);
    else if (nonFood.length === 0) toast.info("All ingredients already exist!");
  };

  const removeIngredientFromCategory = (category, ingredientName) => {
    const lower = ingredientName.toLowerCase();
    if (categorizedWithQty) setCategorizedWithQty(prev => ({ ...prev, [category]: prev[category].filter(i => i.name.toLowerCase() !== lower) }));
    if (categorizedIngredients) setCategorizedIngredients(prev => ({ ...prev, [category]: prev[category].filter(i => i.toLowerCase() !== lower) }));
    setIngredients(prev => prev.filter(i => i.toLowerCase() !== lower));
    setIngredientsWithQty(prev => prev.filter(i => i.name.toLowerCase() !== lower));
  };

  const removeIngredient = (index) => {
    const name = ingredients[index];
    const lower = name.toLowerCase();
    setIngredients(ingredients.filter((_, i) => i !== index));
    setIngredientsWithQty(prev => prev.filter(i => i.name.toLowerCase() !== lower));
    if (categorizedWithQty) {
      for (const cat of Object.keys(categorizedWithQty)) {
        if (categorizedWithQty[cat].some(i => i.name.toLowerCase() === lower)) {
          setCategorizedWithQty(prev => ({ ...prev, [cat]: prev[cat].filter(i => i.name.toLowerCase() !== lower) }));
          break;
        }
      }
    }
    if (categorizedIngredients) {
      for (const cat of Object.keys(categorizedIngredients)) {
        if (categorizedIngredients[cat].some(i => i.toLowerCase() === lower)) {
          setCategorizedIngredients(prev => ({ ...prev, [cat]: prev[cat].filter(i => i.toLowerCase() !== lower) }));
          break;
        }
      }
    }
  };

  // ─── Edit Quantity ───
  const openEditQuantity = (ingredientName, category = null) => {
    const existing = ingredientsWithQty.find(i => i.name.toLowerCase() === ingredientName.toLowerCase());
    setEditingIngredient({ name: ingredientName, category, original: existing });
    setEditQuantity(existing?.quantity || "");
    setEditUnit(existing?.unit || "");
  };

  const saveEditedQuantity = () => {
    if (!editingIngredient) return;
    const { name, category } = editingIngredient;
    const display = editQuantity ? `${editQuantity}${editUnit && editUnit !== 'pieces' ? ' ' + editUnit : ''} ${name}` : name;
    setIngredientsWithQty(prev => {
      const exists = prev.find(i => i.name.toLowerCase() === name.toLowerCase());
      return exists
        ? prev.map(i => i.name.toLowerCase() === name.toLowerCase() ? { ...i, quantity: editQuantity || null, unit: editUnit || null, display } : i)
        : [...prev, { name, quantity: editQuantity || null, unit: editUnit || null, display }];
    });
    if (category && categorizedWithQty) {
      setCategorizedWithQty(prev => ({
        ...prev,
        [category]: prev[category].map(i => i.name.toLowerCase() === name.toLowerCase() ? { ...i, quantity: editQuantity || null, unit: editUnit || null, display } : i)
      }));
    }
    setEditingIngredient(null);
    setEditQuantity("");
    setEditUnit("");
  };

  // ─── Recipe Generation ───
  const generateSingleRecipe = async (ingredientsList = null) => {
    const toUse = ingredientsList || ingredients;
    if (!selectedVibe || toUse.length === 0) return;
    setAppState(STATES.GENERATING);
    setLoadingStage("Finding the perfect recipe...");
    try {
      const activeDiets = Object.entries(dietModes).filter(([_, a]) => a).map(([d]) => d);
      setLoadingStage(`Analyzing your ${selectedVibe} vibe...`);
      await new Promise(r => setTimeout(r, 400));
      setLoadingStage(`Matching ${toUse.length} ingredients...`);
      await new Promise(r => setTimeout(r, 400));
      const cuisineText = selectedCuisine ? `${selectedCuisine} ` : "";
      const dishLabel = DISH_TYPES.find(d => d.id === selectedDishType)?.label || "Main Course";
      setLoadingStage(`Creating your ${cuisineText}${dishLabel.toLowerCase()}...`);
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      let ingredientsWithQtyPayload = null;
      if (ingredientsWithQty.length > 0) {
        const qtyMap = {};
        ingredientsWithQty.forEach(i => { qtyMap[i.name.toLowerCase()] = i; });
        ingredientsWithQtyPayload = toUse.map(name => {
          const q = qtyMap[name.toLowerCase()];
          return q ? { name: q.name, quantity: q.quantity, unit: q.unit } : { name, quantity: null, unit: null };
        });
      }
      const response = await axios.post(`${API}/generate-single-recipe`, {
        ingredients: toUse,
        ingredients_with_qty: ingredientsWithQtyPayload,
        vibe: selectedVibe,
        vegetarian: dietModes.vegetarian || dietModes.vegan,
        vegan: dietModes.vegan,
        glutenFree: dietModes.glutenFree,
        keto: dietModes.keto,
        cuisine: selectedCuisine,
        dishType: selectedDishType,
        session_id: sessionId,
        recipe_index: 0,
        exclude_titles: []
      });
      setLoadingStage("Plating your recipe...");
      await new Promise(r => setTimeout(r, 300));
      const newRecipe = response.data.recipe;
      setRecipes([newRecipe]);
      addToHistory([newRecipe], selectedVibe, activeDiets);
      setAppState(STATES.RECIPES);
      setLoadingStage("");
    } catch (error) {
      console.error("Error generating recipe:", error);
      toast.error(
        <div className="flex items-center gap-2">
          <span>Failed to generate recipe.</span>
          <button onClick={() => openErrorFeedback("Recipe generation failed")} className="underline font-bold">Report this</button>
        </div>,
        { duration: 8000 }
      );
      setAppState(STATES.VIBE);
      setLoadingStage("");
    }
  };

  const generateRecipes = async () => {
    if (selectedDishType === "dessert") {
      setDessertIngredients([...ingredients]);
      setShowDessertConfirm(true);
      return;
    }
    await generateSingleRecipe();
  };

  const handleDessertConfirm = async () => {
    const selected = [...dessertIngredients];
    setIngredients(selected);
    setShowDessertConfirm(false);
    await generateSingleRecipe(selected);
  };

  const toggleDessertIngredient = (ingredient) => {
    setDessertIngredients(prev => prev.includes(ingredient) ? prev.filter(i => i !== ingredient) : [...prev, ingredient]);
  };

  const addDessertIngredient = () => {
    const trimmed = newDessertIngredient.trim().toLowerCase();
    if (trimmed && !dessertIngredients.includes(trimmed)) {
      setDessertIngredients(prev => [...prev, trimmed]);
      setNewDessertIngredient("");
    }
  };

  // ─── Navigation ───
  const resetApp = () => {
    setAppState(STATES.HOME);
    setIngredients([]);
    setIngredientsWithQty([]);
    setCategorizedIngredients(null);
    setCategorizedWithQty(null);
    setSelectedVibe(null);
    setSelectedCuisine(null);
    setSelectedDishType("main_course");
    setDietModes({ vegetarian: false, vegan: false, glutenFree: false, keto: false });
    setRecipes([]);
    setSelectedRecipe(null);
    setSafetyMessage(null);
    setLoadingStage("");
    uploadedImages.forEach(img => URL.revokeObjectURL(img.preview));
    setUploadedImages([]);
  };

  const goBackToPhotos = () => {
    setAppState(STATES.HOME);
    setIngredients([]);
    setIngredientsWithQty([]);
    setCategorizedIngredients(null);
    setCategorizedWithQty(null);
    setSafetyMessage(null);
    setLoadingStage("");
  };

  const handleManualEntry = () => {
    setIngredients([]);
    setIngredientsWithQty([]);
    const empty = { vegetables: [], fruits: [], meats_seafood: [], dairy_eggs: [], grains_bread: [], pantry_other: [] };
    setCategorizedIngredients({ ...empty });
    setCategorizedWithQty({ ...empty });
    setUploadedImages([]);
    setSafetyMessage(null);
    setAppState(STATES.INGREDIENTS);
  };

  const toggleDietMode = (mode) => setDietModes(prev => ({ ...prev, [mode]: !prev[mode] }));

  // ─── Render ───
  return (
    <div className="min-h-screen bg-[#FEFAE0]">
      <div className="noise-overlay" />
      <Toaster position="top-center" richColors />
      
      <div className="mobile-container">
        <AnimatePresence mode="wait">
          {appState === STATES.HOME && (
            <HomeScreen
              uploadedImages={uploadedImages}
              setUploadedImages={setUploadedImages}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              isDragActive={isDragActive}
              onDrop={onDrop}
              analyzeImages={analyzeImages}
              handleManualEntry={handleManualEntry}
              openFeedback={openFeedback}
              savedRecipes={savedRecipes}
              setShowSaved={setShowSaved}
              recipeHistory={recipeHistory}
              setShowHistory={setShowHistory}
              removeUploadedImage={removeUploadedImage}
            />
          )}

          {appState === STATES.SCANNING && (
            <ScanningScreen
              loadingStage={loadingStage}
              currentScanningMessage={currentScanningMessage}
              resetApp={resetApp}
            />
          )}

          {appState === STATES.INGREDIENTS && (
            <IngredientsScreen
              ingredients={ingredients}
              categorizedIngredients={categorizedIngredients}
              categorizedWithQty={categorizedWithQty}
              uploadedImages={uploadedImages}
              safetyMessage={safetyMessage}
              newIngredient={newIngredient}
              setNewIngredient={setNewIngredient}
              newIngredientCategory={newIngredientCategory}
              setNewIngredientCategory={setNewIngredientCategory}
              bulkInput={bulkInput}
              setBulkInput={setBulkInput}
              addIngredient={addIngredient}
              addBulkIngredients={addBulkIngredients}
              removeIngredient={removeIngredient}
              removeIngredientFromCategory={removeIngredientFromCategory}
              openEditQuantity={openEditQuantity}
              goBackToPhotos={goBackToPhotos}
              goToVibe={() => setAppState(STATES.VIBE)}
              validatingIngredient={validatingIngredient}
            />
          )}

          {appState === STATES.VIBE && (
            <VibeScreen
              selectedVibe={selectedVibe}
              setSelectedVibe={setSelectedVibe}
              selectedCuisine={selectedCuisine}
              setSelectedCuisine={setSelectedCuisine}
              selectedDishType={selectedDishType}
              setSelectedDishType={setSelectedDishType}
              dietModes={dietModes}
              toggleDietMode={toggleDietMode}
              generateRecipes={generateRecipes}
              goBackToIngredients={() => setAppState(STATES.INGREDIENTS)}
            />
          )}

          {appState === STATES.GENERATING && (
            <GeneratingScreen
              selectedVibe={selectedVibe}
              loadingStage={loadingStage}
              currentLoadingMessage={currentLoadingMessage}
              setAppState={setAppState}
            />
          )}

          {appState === STATES.RECIPES && recipes.length > 0 && (
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-[#FEFAE0]">
                <div className="w-12 h-12 border-4 border-[#FFB703]/30 border-t-[#FFB703] rounded-full animate-spin" />
              </div>
            }>
              <SwipeableRecipeViewer
                initialRecipe={recipes[0]}
                ingredients={ingredients}
                vibe={selectedVibe}
                dietModes={dietModes}
                cuisine={selectedCuisine}
                dishType={selectedDishType}
                onBack={() => setAppState(STATES.VIBE)}
                onHome={resetApp}
                onRecipeSelect={setSelectedRecipe}
                savedRecipes={savedRecipes}
                toggleSaveRecipe={toggleSaveRecipe}
                isRecipeSaved={isRecipeSaved}
                copyRecipeToClipboard={copyRecipeToClipboard}
                shareRecipe={shareRecipe}
                copiedRecipeId={copiedRecipeId}
                addToHistory={addToHistory}
                openErrorFeedback={openErrorFeedback}
              />
            </Suspense>
          )}
        </AnimatePresence>

        {/* Modals */}
        <RecipeDetailModal
          selectedRecipe={selectedRecipe}
          setSelectedRecipe={setSelectedRecipe}
          copiedRecipeId={copiedRecipeId}
          copyRecipeToClipboard={copyRecipeToClipboard}
          shareRecipe={shareRecipe}
          toggleSaveRecipe={toggleSaveRecipe}
          isRecipeSaved={isRecipeSaved}
          openRecipeFeedback={openRecipeFeedback}
        />

        <SavedRecipesModal
          showSaved={showSaved}
          setShowSaved={setShowSaved}
          savedRecipes={savedRecipes}
          toggleSaveRecipe={toggleSaveRecipe}
          setSelectedRecipe={setSelectedRecipe}
        />

        <HistoryModal
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          recipeHistory={recipeHistory}
          setRecipeHistory={setRecipeHistory}
          setSelectedRecipe={setSelectedRecipe}
        />

        {/* Feedback Button */}
        <button
          onClick={() => setShowFeedbackWall(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 bg-[#3A8D85] text-white rounded-full font-bold hover:opacity-90 transition-all"
          style={{ boxShadow: "4px 4px 0px 0px rgba(38, 70, 83, 1)" }}
          data-testid="feedback-btn"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm">Feedback</span>
        </button>
      </div>

      {/* Feedback Wall */}
      {showFeedbackWall && (
        <div className="fixed inset-0 z-[100]">
          <Suspense fallback={
            <div className="min-h-screen bg-[#FEFAE0] flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-[#FFB703]/30 border-t-[#FFB703] rounded-full animate-spin" />
            </div>
          }>
            <FeedbackWall 
              onBack={() => setShowFeedbackWall(false)} 
              initialCategory={feedbackCategory}
              linkedRecipe={feedbackRecipe}
              sessionContext={feedbackContext}
            />
          </Suspense>
        </div>
      )}

      <DessertConfirmModal
        showDessertConfirm={showDessertConfirm}
        setShowDessertConfirm={setShowDessertConfirm}
        dessertIngredients={dessertIngredients}
        toggleDessertIngredient={toggleDessertIngredient}
        newDessertIngredient={newDessertIngredient}
        setNewDessertIngredient={setNewDessertIngredient}
        addDessertIngredient={addDessertIngredient}
        handleDessertConfirm={handleDessertConfirm}
        ingredients={ingredients}
      />

      <EditQuantityModal
        editingIngredient={editingIngredient}
        setEditingIngredient={setEditingIngredient}
        editQuantity={editQuantity}
        setEditQuantity={setEditQuantity}
        editUnit={editUnit}
        setEditUnit={setEditUnit}
        saveEditedQuantity={saveEditedQuantity}
      />
    </div>
  );
}

export default App;
